// Signed media URLs expire (G5, S1, #123: about an hour after the read that carries them). A read
// that carries them names when in `urls_expire_at`; these keep such a read fresh, so a page left
// open never shows a broken picture. The generation feature's job reads and the brands use them.

/** Signed URLs are read again this long before they stop working (about 2 minutes). */
export const URL_MARGIN_MS = 120_000;
/** The shortest wait for fresh URLs, so a clock that runs ahead of the server cannot loop. */
export const URL_MIN_WAIT_MS = 60_000;

type Expiry = string | null | undefined;

/** How long until URLs that expire at `expiresAt` should be read again; false without any. */
export function urlRefreshIn(expiresAt: Expiry, now: number = Date.now()): number | false {
  if (expiresAt === null || expiresAt === undefined) return false;
  return Math.max(Date.parse(expiresAt) - URL_MARGIN_MS - now, URL_MIN_WAIT_MS);
}

/** True when the URLs are within the margin of expiring, or past it. */
export function urlsDue(expiresAt: Expiry, now: number = Date.now()): boolean {
  if (expiresAt === null || expiresAt === undefined) return false;
  return Date.parse(expiresAt) - URL_MARGIN_MS <= now;
}

/** The sooner of several waits; false when none is set. */
export function soonest(...waits: (number | false)[]): number | false {
  const due = waits.filter((wait): wait is number => wait !== false);
  return due.length === 0 ? false : Math.min(...due);
}

/** The soonest of several reads' expiries (a list's items), or null when none has URLs. */
export function soonestExpiry(expiries: readonly Expiry[]): string | null {
  const times = expiries.flatMap((each) => (each === null || each === undefined ? [] : [each]));
  if (times.length === 0) return null;
  return new Date(Math.min(...times.map((time) => Date.parse(time)))).toISOString();
}

/**
 * TanStack Query options that keep a read's signed URLs fresh: it is read again shortly before
 * they expire, and on window focus once they are due. `expiryOf` names the read's expiry.
 */
export function freshUrls<T>(expiryOf: (data: T) => Expiry) {
  const expiry = (data: T | undefined) => (data === undefined ? null : expiryOf(data));
  return {
    refetchInterval: (query: { state: { data: T | undefined } }) =>
      urlRefreshIn(expiry(query.state.data)),
    refetchOnWindowFocus: (query: { state: { data: T | undefined } }) =>
      urlsDue(expiry(query.state.data)) ? ("always" as const) : false,
  };
}

/** When a signed media URL stops working, from Cloud CDN's `Expires` (Unix seconds), as an ISO
 *  time; null for a URL that names none (the local and fake buckets). */
export function signedExpiry(url: string | null): string | null {
  if (url === null) return null;
  const query = url.indexOf("?");
  if (query < 0) return null;
  const expires = Number(new URLSearchParams(url.slice(query + 1)).get("Expires") ?? Number.NaN);
  return Number.isFinite(expires) ? new Date(expires * 1000).toISOString() : null;
}

/** A URL without its query: the file itself, whatever its signature. */
const fileOf = (url: string) => url.split("?", 1)[0] ?? url;

/**
 * The URL to show for a file a read names again: the one already shown while it is the same file
 * and stays good past the margin, else the read's. Each read signs again, and the expiry steps
 * every 15 minutes (S1), so without this every refetch after a step would change each picture's
 * `src` and the browser would fetch the whole grid again (#794).
 */
export function keptUrl(shown: string | null, read: string | null, now: number = Date.now()): string | null {
  if (shown === null || read === null || shown === read || fileOf(shown) !== fileOf(read)) return read;
  const expiry = signedExpiry(shown);
  return expiry === null || urlsDue(expiry, now) ? read : shown;
}
